import React from 'react';
import { createRoot } from 'react-dom/client';
import { createStore } from './configureStore';

/*
 * Allow for React components to be sprinkled in HTML and loaded dynamically
 *
 * Component can either be in same dir as this file or in index.js in a component dir:
 * AddFilesFormContainer.js or AddFilesFormContainer/index.js
 *
 * example:
 *   <div data-react="AddFilesFormContainer"></div>
 *
 * example with props:
 *   <div
 *      data-react="FormFieldText"
 *      data-react-props='{"label": "LABEL", "name":"SOMTHING"}'
 *   ></div>
 *
 */

const getComponent = (context, componentName) => {
    const findComponentDir = new RegExp(`${componentName}(/index)?\\.js`);
    const componentDir = context.keys().find(path => path.match(findComponentDir));
    if (!componentDir) {
        return null;
    }

    return context(componentDir).default;
};

const getSprinkleNodes = () => Array.from(document.querySelectorAll('[data-react]'));

export function initializeSprinkles(getComponentContext) {
    function getSprinkleReducers() {
        const nodes = getSprinkleNodes();
        const context = getComponentContext();

        return nodes
            .filter(node => {
                const Comp = getComponent(context, node.dataset.react);
                return !!Comp;
            })
            .reduce((obj, node) => {
                const Comp = getComponent(context, node.dataset.react);
                if (Comp.reducers) {
                    return Object.assign(obj, Comp.reducers);
                }

                return obj;
            }, {});
    }

    function getSprinkleComponents() {
        const nodes = getSprinkleNodes();
        const context = getComponentContext();

        return nodes
            .filter(node => {
                const Comp = getComponent(context, node.dataset.react);
                return !!Comp;
            })
            .map(node => {
                const Comp = getComponent(context, node.dataset.react);
                const props = node.dataset.reactProps ? JSON.parse(node.dataset.reactProps) : {};
                props.children = node.innerHTML;
                return { Comp, props, node };
            });
    }

    const reducers = getSprinkleReducers(getComponentContext);
    let store = null;
    if (Object.keys(reducers).length > 0) {
        store = createStore(reducers);
    }

    document.addEventListener('DOMContentLoaded', async () => {
        const sprinkles = getSprinkleComponents(getComponentContext);

        for (const sprinkle of sprinkles) {
            const { Comp, props, node } = sprinkle;
            const root = createRoot(node);

            if (store) {
                root.render(<Comp store={store} {...props} />);
            } else {
                root.render(<Comp {...props} />);
            }
        }
    });
}
